import React from 'react';
import {
  Box, Typography, TextField, FormControlLabel, Switch,
  Chip, Autocomplete, Checkbox,
} from '@mui/material';
import type { FootballSettings, FootballTieBreaker } from '@/types';
import type { SportSettingsFormProps } from './types';
import { SettingsSection } from './SettingsSection';
import { SettingsField, SettingsFieldsGrid } from './SettingsFieldsGrid';

const TIEBREAKER_OPTIONS: { value: FootballTieBreaker; label: string }[] = [
  { value: 'goal_difference',      label: 'Разница мячей' },
  { value: 'goals_scored',         label: 'Забитые мячи' },
  { value: 'goals_conceded',       label: 'Пропущенные мячи' },
  { value: 'head_to_head',         label: 'Личные встречи' },
  { value: 'away_goals',           label: 'Голы на выезде' },
  { value: 'disciplinary_points',  label: 'Дисциплинарные очки' },
  { value: 'lots',                 label: 'Жребий' },
];

export const FootballSettingsForm: React.FC<SportSettingsFormProps> = ({ value, onChange, disabled }) => {
  const s = value as FootballSettings;
  const upd = (patch: Partial<FootballSettings>) => onChange({ ...s, ...patch });

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>

      <SettingsSection title="Состав">
        <SettingsFieldsGrid>
          <SettingsField>
            <TextField label="Игроков на поле" type="number" size="small" fullWidth
              value={s.playersOnField ?? 11}
              onChange={(e) => upd({ playersOnField: parseInt(e.target.value) || 11 })}
              slotProps={{ htmlInput: { min: 1, max: 11 } }}
              disabled={disabled} />
          </SettingsField>
          <SettingsField>
            <TextField label="Игроков в заявке" type="number" size="small" fullWidth
              value={s.squadSize ?? 18}
              onChange={(e) => upd({ squadSize: parseInt(e.target.value) || 18 })}
              slotProps={{ htmlInput: { min: 1, max: 50 } }}
              disabled={disabled} />
          </SettingsField>
          <SettingsField>
            <TextField label="Замен за матч" type="number" size="small" fullWidth
              value={s.substitutionsPerMatch ?? 5}
              onChange={(e) => upd({ substitutionsPerMatch: parseInt(e.target.value) || 5 })}
              slotProps={{ htmlInput: { min: 0, max: 11 } }}
              disabled={disabled} />
          </SettingsField>
        </SettingsFieldsGrid>
      </SettingsSection>

      <SettingsSection title="Очки">
        <SettingsFieldsGrid>
          <SettingsField>
            <TextField label="За победу" type="number" size="small" fullWidth
              value={s.pointsWin ?? 3}
              onChange={(e) => upd({ pointsWin: parseInt(e.target.value) })}
              slotProps={{ htmlInput: { min: 0 } }}
              disabled={disabled} />
          </SettingsField>
          <SettingsField>
            <TextField label="За ничью" type="number" size="small" fullWidth
              value={s.pointsDraw ?? 1}
              onChange={(e) => upd({ pointsDraw: parseInt(e.target.value) })}
              slotProps={{ htmlInput: { min: 0 } }}
              disabled={disabled} />
          </SettingsField>
          <SettingsField>
            <TextField label="За поражение" type="number" size="small" fullWidth
              value={s.pointsLoss ?? 0}
              onChange={(e) => upd({ pointsLoss: parseInt(e.target.value) })}
              slotProps={{ htmlInput: { min: 0 } }}
              disabled={disabled} />
          </SettingsField>
        </SettingsFieldsGrid>
      </SettingsSection>

      <SettingsSection title="Правила матча">
        <SettingsFieldsGrid>
          <SettingsField>
            <TextField label="Длительность матча, мин" type="number" size="small" fullWidth
              value={s.matchDurationMin ?? 90}
              onChange={(e) => upd({ matchDurationMin: parseInt(e.target.value) || 90 })}
              slotProps={{ htmlInput: { min: 1 } }}
              disabled={disabled} />
          </SettingsField>
        </SettingsFieldsGrid>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, flexWrap: 'wrap', gap: 1, mt: 2 }}>
          <FormControlLabel
            control={
              <Switch checked={s.extraTime ?? true} onChange={(e) => upd({ extraTime: e.target.checked })} disabled={disabled} />
            }
            label="Дополнительное время"
          />
          <FormControlLabel
            control={
              <Switch checked={s.penaltyShootout ?? true} onChange={(e) => upd({ penaltyShootout: e.target.checked })} disabled={disabled} />
            }
            label="Серия пенальти"
          />
          <FormControlLabel
            control={
              <Switch checked={s.offside ?? true} onChange={(e) => upd({ offside: e.target.checked })} disabled={disabled} />
            }
            label="Правило офсайда"
          />
        </Box>
      </SettingsSection>

      <SettingsSection title="Тай-брейки (порядок применения при равенстве очков)">
        <Autocomplete
          multiple
          disableCloseOnSelect
          options={TIEBREAKER_OPTIONS.map((o) => o.value)}
          value={s.tieBreakers ?? []}
          onChange={(_, v) => upd({ tieBreakers: v as FootballTieBreaker[] })}
          getOptionLabel={(o) => TIEBREAKER_OPTIONS.find((x) => x.value === o)?.label ?? o}
          disabled={disabled}
          renderOption={(props, option, { selected }) => (
            <li {...props}>
              <Checkbox checked={selected} size="small" sx={{ mr: 1 }} />
              {TIEBREAKER_OPTIONS.find((x) => x.value === option)?.label ?? option}
            </li>
          )}
          renderTags={(val, getTagProps) =>
            val.map((opt, idx) => (
              <Chip
                label={`${idx + 1}. ${TIEBREAKER_OPTIONS.find((x) => x.value === opt)?.label ?? opt}`}
                size="small"
                {...getTagProps({ index: idx })}
                key={opt}
              />
            ))
          }
          renderInput={(params) => (
            <TextField {...params} size="small" fullWidth placeholder="Добавить критерий..." />
          )}
        />
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
          Перечислите критерии в порядке приоритета
        </Typography>
      </SettingsSection>

    </Box>
  );
};
